import { useMemo } from 'react';
import {
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  createTableColumn,
  useTableFeatures,
  type TableCellProps,
} from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import AvatarGroup from '@modules/users/components/AvatarGroup';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import { AppLink } from '@shared/components/AppLink';
import { SmartTableCell } from '@shared/components/tables/SmartTableCell';
import ArchiveTableAction from '@shared/components/tables/ArchiveTableAction';
import { DateLabel } from '@shared/components/dates/DateLabel';
import { usePersistedColumnSizing } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { getChangeTemplatePath } from '../paths';
import { CHANGE_TEMPLATES_TABLE_ID } from '../constants';
import type { TChangeTemplateListItem } from '../types';

type TColumnId = 'title' | 'modified' | 'version' | 'assessments' | 'authors';

type TProps = {
  changeTemplates: TChangeTemplateListItem[];
  onArchive: (changeTemplate: TChangeTemplateListItem) => void;
};

// autoFitColumns stays false - true would re-run width calculation on every render and overwrite user drag-resize.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    title: { defaultWidth: 320, minWidth: 160 },
    modified: { defaultWidth: 110, minWidth: 90 },
    version: { defaultWidth: 110, minWidth: 90 },
    assessments: { defaultWidth: 120, minWidth: 90 },
    authors: { defaultWidth: 140, minWidth: 100 },
  },
};

// Like the folder view: Modified and the latest version follow the name.
const columnIds: TColumnId[] = ['title', 'modified', 'version', 'assessments', 'authors'];

const ChangeTemplateTitleCell = ({
  changeTemplate,
  cellProps,
  onArchive,
}: {
  changeTemplate: TChangeTemplateListItem;
  cellProps: TableCellProps;
  onArchive: (changeTemplate: TChangeTemplateListItem) => void;
}) => {
  const { canEdit } = useCanEdit(changeTemplate.accessRight);
  return (
    <SmartTableCell
      cellProps={cellProps}
      variant={{ kind: 'link', href: getChangeTemplatePath(changeTemplate._id), label: changeTemplate.title }}
      openMode="sameTab"
      tooltip={{ threshold: 70 }}
      after={canEdit && <ArchiveTableAction label="Archive" onArchive={() => onArchive(changeTemplate)} />}
    />
  );
};

const ChangeTemplatesTable = ({ changeTemplates, onArchive }: TProps) => {
  const columns = useMemo(
    () => columnIds.map((columnId) => createTableColumn<TChangeTemplateListItem>({ columnId })),
    []
  );
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items: changeTemplates, getRowId: (changeTemplate) => changeTemplate._id },
    [usePersistedColumnSizing(CHANGE_TEMPLATES_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();

  const tableClasses = useItemTableStyles();
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('title')}>
              <Body1Strong>Title</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('modified')}>
              <Body1Strong>Modified</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('version')}>
              <Body1Strong>Version</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('assessments')}>
              <Body1Strong>Assessments</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('authors')}>
              <Body1Strong>Authors</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item: changeTemplate }) => (
            <TableRow key={changeTemplate._id}>
              <ChangeTemplateTitleCell
                changeTemplate={changeTemplate}
                cellProps={columnSizing_unstable.getTableCellProps('title')}
                onArchive={onArchive}
              />
              <TableCell {...columnSizing_unstable.getTableCellProps('modified')}>
                <DateLabel short>{changeTemplate.stats.modifiedDate}</DateLabel>
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('version')}>
                <div className="flex items-center gap-2">
                  {changeTemplate.latestVersionId ? (
                    // Without a draft, the latest version is the current one, which opens without a version.
                    <AppLink
                      to={getChangeTemplatePath(changeTemplate._id, {
                        templateVersion: changeTemplate.hasDraft ? changeTemplate.latestVersionId : undefined,
                      })}
                    >
                      {changeTemplate.latestVersionId}
                    </AppLink>
                  ) : (
                    <span>-</span>
                  )}
                  {changeTemplate.hasDraft && (
                    <Tooltip
                      content={`This ${TERMINOLOGY.changeTemplate.lower} has an open draft.`}
                      relationship="label"
                    >
                      <EditRegular />
                    </Tooltip>
                  )}
                </div>
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('assessments')}>
                {changeTemplate.assessmentCount}
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('authors')}>
                {changeTemplate.authors ? <AvatarGroup ids={changeTemplate.authors} /> : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default ChangeTemplatesTable;
